import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';

import { useQueryParams } from 'src/shared/hooks/useQueryParams';
import { AssignedProviderSelection } from 'src/modules/providers/components/AssignedProviderSelection';
import { getMyProvidersForScheduleSessionFlow } from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType } from 'src/modules/user/models/user';
import { getConfigForProviderType } from 'src/modules/providers/helpers/getConfigForProviderType';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useProvider } from 'src/modules/providers/hooks/useProvider';
import { HpiEligibilityFlow } from 'src/shared/models/shared';

const VALID_PROVIDER_TYPE_FILTERS = [
    ProviderType.COACH.toLowerCase(),
    ProviderType.THERAPIST.toLowerCase(),
    ProviderType.PSYCHIATRIST.toLowerCase(),
];

export const ScheduleSessionProviderSelectionPage = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const { hideSessionSchedulingWeb } = useFlags();
    const queryParams = useQueryParams();
    const [isLoadingProviders, setIsLoadingProviders] = useState(true);
    const { fetchMyProviders } = useProvider();
    const myProviders = useSelector(getMyProvidersForScheduleSessionFlow);
    const {
        coveredCoachingVisits,
        coachingVisitsRemaining,
        therapyVisitsRemaining,
        coveredTherapyVisits,
    } = useSelector(getSessionUtilizationInfo);
    const user = useSelector(getUserInformation);

    const providerTypeFilter = queryParams.get('providerType')?.toLowerCase();

    const providers = useMemo(
        () =>
            providerTypeFilter && VALID_PROVIDER_TYPE_FILTERS.includes(providerTypeFilter)
                ? myProviders.filter(p => p.providerType?.toLowerCase() === providerTypeFilter)
                : myProviders,
        [myProviders, providerTypeFilter],
    );

    const providerUuid: string | undefined =
        providers.length === 1 ? providers[0].uuid : queryParams.get('providerUuid');

    const getNextLocationDescriptor = useCallback(
        (providerUuid: string): { pathname: string; state?: Record<string, unknown> } => {
            const provider = providers.find(({ uuid }) => uuid === providerUuid);
            const providerToSessionsLeft = {
                [ProviderType.COACH]: coachingVisitsRemaining,
                [ProviderType.THERAPIST]: therapyVisitsRemaining,
            };
            const providerToCoveredVisits = {
                [ProviderType.COACH]: coveredCoachingVisits,
                [ProviderType.THERAPIST]: coveredTherapyVisits,
            };
            const sessionsLeft = getConfigForProviderType(provider, providerToSessionsLeft, 0);
            const sessionsCovered = getConfigForProviderType(provider, providerToCoveredVisits, 0);

            if (
                sessionsLeft === 0 &&
                provider?.providerType === ProviderType.THERAPIST &&
                user.isCompanyHpiEnabled &&
                !user.hpiEligibilityStatus
            ) {
                return {
                    pathname: '/extend-therapy-sessions',
                };
            }

            if (
                provider?.providerType === ProviderType.PSYCHIATRIST &&
                !user.isCompanyPsychiatryNonHpiEnabled &&
                !user.hpiEligibilityStatus
            ) {
                return {
                    pathname: '/inquire-employer-insurance-coverage',
                    state: {
                        hpiEligibilityFlow: HpiEligibilityFlow.Psychiatry,
                    },
                };
            }

            return {
                pathname: '/schedule-session/calendar',
                state: {
                    coachUuid: providerUuid,
                    isFirstSession: sessionsLeft === sessionsCovered,
                },
            };
        },
        [
            providers,
            coveredCoachingVisits,
            coachingVisitsRemaining,
            therapyVisitsRemaining,
            coveredTherapyVisits,
            user.isCompanyHpiEnabled,
            user.isCompanyPsychiatryNonHpiEnabled,
            user.hpiEligibilityStatus,
        ],
    );

    const handleProviderPress = useCallback(
        (providerUuid: string) => history.push(getNextLocationDescriptor(providerUuid)),
        [getNextLocationDescriptor, history],
    );

    // Fetch last changes of providers in case the displayOnSchedule has
    // changed
    useEffect(() => {
        if (hideSessionSchedulingWeb) return;

        fetchMyProviders()
            .then(() => setIsLoadingProviders(false))
            .catch(() => setIsLoadingProviders(false));
    }, [fetchMyProviders, hideSessionSchedulingWeb]);

    if (hideSessionSchedulingWeb) {
        return <Redirect to="/dashboard" />;
    }

    if (isLoadingProviders) {
        return <CenteredSpinner />;
    }

    if (providers.length === 0) {
        return <Redirect to="/care-team" />;
    }

    if (providerUuid && providers.find(({ uuid }) => uuid === providerUuid)) {
        return <Redirect to={getNextLocationDescriptor(providerUuid)} />;
    }

    return (
        <AssignedProviderSelection
            providers={providers}
            onProviderPress={handleProviderPress}
            headerText={t('providers:provider_selection.title.matched.provider')}
            subTitleText={t('providers:provider_selection.description.provider')}
        />
    );
};
